import { useCallback, useEffect, useState } from "react";
import Modal from "../../../../components/common/Modal";
import { BankResponseData } from "../../../../setup/api/services/payment/models";
import { useTranslation } from "react-i18next";
import { checkUserBank } from "../../../../setup/api/services/payment";

// styles
import styles from "../../../../components/contract/cashoutModal/CashoutModal.module.scss";
import { Box, Button, CircularProgress } from "@mui/material";

interface Props {
  showModal: boolean;
  userId: any;
  onClose: () => void;
}

const BankInfoModal = ({ showModal, userId, onClose }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bankDetails, setBankDetails] = useState<
    BankResponseData | undefined
  >();
  const { t } = useTranslation();

  const handleClose = () => {
    onClose();
  };

  const loadUserBankDetails = useCallback(async () => {
    setLoading(true);
    try {
      const bankInfo = await checkUserBank(userId);
      setBankDetails(bankInfo?.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (showModal) {
      loadUserBankDetails();
    }
  }, [showModal, loadUserBankDetails]);

  const renderAccountDetails = useCallback(() => {
    return loading ? (
      <Box className="center">
        <CircularProgress />
      </Box>
    ) : (
      <>
        <div className={styles["details"]}>
          <p style={{ fontWeight: "bold" }}>
            {t("lbl_user_bank_account_number")}
          </p>
          {bankDetails ? (
            <p className={styles["detail_desc"]}>{bankDetails?.iban}</p>
          ) : (
            <p className={styles["pendingText"]}>
              {t("lbl_user_bank_unavailable")}
            </p>
          )}
        </div>
        <div className={styles["details"]}>
          <p style={{ fontWeight: "bold" }}>
            {t("lbl_user_bank_beneficiary_name")}
          </p>
          {bankDetails ? (
            <p className={styles["detail_desc"]}>
              {bankDetails?.beneficiaryName}
            </p>
          ) : (
            <p className={styles["pendingText"]}>
              {t("lbl_user_bank_unavailable")}
            </p>
          )}
        </div>
        <div className={styles["details"]}>
          <p style={{ fontWeight: "bold" }}>{t("lbl_user_bank_title")}</p>
          {bankDetails ? (
            <p className={styles["detail_desc"]}>
              {bankDetails?.bank?.arabicName}
            </p>
          ) : (
            <p className={styles["pendingText"]}>
              {t("lbl_user_bank_unavailable")}
            </p>
          )}
        </div>
        <div className={styles["details"]}>
          <p style={{ fontWeight: "bold" }}>
            {t("lbl_user_bank_selected_bank")}
          </p>
          {bankDetails ? (
            <p className={styles["detail_desc"]}>
              {bankDetails?.selectedBank?.arabicName}
            </p>
          ) : (
            <p className={styles["pendingText"]}>
              {t("lbl_user_bank_unavailable")}
            </p>
          )}
        </div>
      </>
    );
  }, [bankDetails, loading, t]);

  return (
    <Modal size={"small"} visible={showModal} onCancel={handleClose}>
      <h1>{t("lbl_user_bank_info_header")}</h1>
      {renderAccountDetails()}
      <Button
        variant="outlined"
        color="primary"
        fullWidth={true}
        onClick={handleClose}>
        {t("lbl_close_button")}
      </Button>
    </Modal>
  );
};

export default BankInfoModal;
